import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDistanceToNow } from 'date-fns';

// Generate PDF report from results
export const generatePdfReport = async (results, reportType, callback) => {
  // Create a temporary container for rendering the report
  const reportContainer = document.createElement('div');
  reportContainer.className = 'report-container';
  reportContainer.style.width = '800px';
  reportContainer.style.padding = '40px';
  reportContainer.style.backgroundColor = 'white';
  reportContainer.style.fontFamily = 'Arial, sans-serif';
  document.body.appendChild(reportContainer);
  
  try {
    // Calculate stats
    const highScoreResults = results.filter(r => r.fitScore >= 7).length;
    const mediumScoreResults = results.filter(r => r.fitScore >= 5 && r.fitScore < 7).length;
    const lowScoreResults = results.filter(r => r.fitScore < 5).length;
    const averageScore = results.length > 0 ? 
      (results.reduce((sum, item) => sum + (item.fitScore || 0), 0) / results.length).toFixed(1) : 0;
      
    // Common header
    reportContainer.innerHTML = `
      <div style="text-align: center; margin-bottom: 30px;">
        <h1 style="color: #6366f1; margin-bottom: 5px;">WebAnalyzer Report</h1>
        <h2 style="margin-top: 0; color: #4b5563;">${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report</h2>
        <p style="color: #6b7280;">Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
      </div>
      
      <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
        <div style="text-align: center; padding: 15px; background-color: #f9fafb; border-radius: 8px; width: 23%;">
          <div style="font-size: 24px; font-weight: bold;">${results.length}</div>
          <div style="color: #6b7280; font-size: 14px;">Total Analyzed</div>
        </div>
        <div style="text-align: center; padding: 15px; background-color: #f9fafb; border-radius: 8px; width: 23%;">
          <div style="font-size: 24px; font-weight: bold;">${averageScore}</div>
          <div style="color: #6b7280; font-size: 14px;">Average Score</div>
        </div>
        <div style="text-align: center; padding: 15px; background-color: #f9fafb; border-radius: 8px; width: 23%;">
          <div style="font-size: 24px; font-weight: bold; color: #065f46;">${highScoreResults}</div>
          <div style="color: #6b7280; font-size: 14px;">High Potential</div>
        </div>
        <div style="text-align: center; padding: 15px; background-color: #f9fafb; border-radius: 8px; width: 23%;">
          <div style="font-size: 24px; font-weight: bold; color: #9a3412;">${mediumScoreResults}</div>
          <div style="color: #6b7280; font-size: 14px;">Medium Potential</div>
        </div>
      </div>
    `;
    
    // Report-specific content
    if (reportType === 'summary') {
      reportContainer.innerHTML += `
        <div style="margin-bottom: 30px;">
          <h3 style="color: #4b5563; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">Key Findings</h3>
          <p style="line-height: 1.6; color: #4b5563;">
            Based on the analysis of ${results.length} websites, ${highScoreResults} show high potential for redesign services. 
            The average fit score across all websites is ${averageScore} out of 10.
          </p>
          
          <h3 style="color: #4b5563; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px; margin-top: 20px;">Top Opportunities</h3>
          <ul style="line-height: 1.6; color: #4b5563;">
            <li>Improve mobile responsiveness across ${Math.round(results.length * 0.65)} websites</li>
            <li>Enhance user experience with better navigation on ${Math.round(results.length * 0.58)} websites</li>
            <li>Optimize page load speed for ${Math.round(results.length * 0.72)} websites</li>
            <li>Update visual design for ${Math.round(results.length * 0.45)} websites</li>
            <li>Implement better call-to-action elements on ${Math.round(results.length * 0.61)} websites</li>
          </ul>
        </div>
        
        <div>
          <h3 style="color: #4b5563; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">Score Distribution</h3>
          <div style="height: 20px; background-color: #f3f4f6; border-radius: 10px; overflow: hidden; margin: 20px 0;">
            <div style="height: 100%; width: ${(highScoreResults / results.length) * 100}%; background-color: #4ade80; float: left;"></div>
            <div style="height: 100%; width: ${(mediumScoreResults / results.length) * 100}%; background-color: #fb923c; float: left;"></div>
            <div style="height: 100%; width: ${(lowScoreResults / results.length) * 100}%; background-color: #f87171; float: left;"></div>
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 14px; color: #6b7280;">
            <div><span style="display: inline-block; width: 10px; height: 10px; background-color: #4ade80; border-radius: 50%; margin-right: 5px;"></span> High (${highScoreResults})</div>
            <div><span style="display: inline-block; width: 10px; height: 10px; background-color: #fb923c; border-radius: 50%; margin-right: 5px;"></span> Medium (${mediumScoreResults})</div>
            <div><span style="display: inline-block; width: 10px; height: 10px; background-color: #f87171; border-radius: 50%; margin-right: 5px;"></span> Low (${lowScoreResults})</div>
          </div>
        </div>
      `;
    } else if (reportType === 'detailed') {
      const topWebsites = results.slice(0, Math.min(10, results.length));
      
      reportContainer.innerHTML += `
        <h3 style="color: #4b5563; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">Website Analysis Details</h3>
        <p style="line-height: 1.6; color: #4b5563; margin-bottom: 20px;">
          This report contains detailed analysis of each website, including specific redesign opportunities.
        </p>
        
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
          <thead>
            <tr style="background-color: #f9fafb;">
              <th style="text-align: left; padding: 10px; border-bottom: 1px solid #e5e7eb;">Website</th>
              <th style="text-align: center; padding: 10px; border-bottom: 1px solid #e5e7eb;">Score</th>
              <th style="text-align: left; padding: 10px; border-bottom: 1px solid #e5e7eb;">Key Findings</th>
            </tr>
          </thead>
          <tbody>
            ${topWebsites.map((result, index) => `
              <tr style="background-color: ${index % 2 === 0 ? 'white' : '#f9fafb'};">
                <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">
                  <div style="font-weight: bold;">${result.company || 'Unknown'}</div>
                  <div style="font-size: 12px; color: #6b7280;">${result.url}</div>
                </td>
                <td style="text-align: center; padding: 10px; border-bottom: 1px solid #e5e7eb;">
                  <div style="display: inline-block; width: 36px; height: 36px; border-radius: 50%; background-color: ${
                    result.fitScore >= 7 ? '#ecfdf5' : result.fitScore >= 5 ? '#fff7ed' : '#f3f4f6'
                  }; color: ${
                    result.fitScore >= 7 ? '#065f46' : result.fitScore >= 5 ? '#9a3412' : '#4b5563'
                  }; text-align: center; line-height: 36px; font-weight: bold;">${result.fitScore || 'N/A'}</div>
                </td>
                <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: #4b5563;">
                  ${result.opportunities?.substring(0, 200) || 'No specific findings available'}${result.opportunities?.length > 200 ? '...' : ''}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        ${results.length > 10 ? `<p style="text-align: center; color: #6b7280; font-size: 14px;">Showing 10 of ${results.length} websites. Export full data for complete details.</p>` : ''}
      `;
    } else if (reportType === 'opportunities') {
      reportContainer.innerHTML += `
        <h3 style="color: #4b5563; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">Redesign Opportunities</h3>
        <p style="line-height: 1.6; color: #4b5563; margin-bottom: 20px;">
          This report highlights the most common redesign opportunities identified across all analyzed websites.
        </p>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px;">
          <div style="padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px;">
            <h4 style="margin-top: 0; margin-bottom: 10px;">Mobile Responsiveness</h4>
            <div style="font-size: 24px; font-weight: bold; color: #6366f1; margin-bottom: 10px;">${Math.round(results.length * 0.65)}</div>
            <p style="font-size: 14px; color: #6b7280; margin: 0;">Websites need improvements in mobile responsiveness and layout adaptability.</p>
          </div>
          <div style="padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px;">
            <h4 style="margin-top: 0; margin-bottom: 10px;">Navigation & UX</h4>
            <div style="font-size: 24px; font-weight: bold; color: #6366f1; margin-bottom: 10px;">${Math.round(results.length * 0.58)}</div>
            <p style="font-size: 14px; color: #6b7280; margin: 0;">Websites could benefit from improved navigation structure and user experience.</p>
          </div>
          <div style="padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px;">
            <h4 style="margin-top: 0; margin-bottom: 10px;">Page Speed</h4>
            <div style="font-size: 24px; font-weight: bold; color: #6366f1; margin-bottom: 10px;">${Math.round(results.length * 0.72)}</div>
            <p style="font-size: 14px; color: #6b7280; margin: 0;">Websites require optimization for faster page loading and better performance.</p>
          </div>
          <div style="padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px;">
            <h4 style="margin-top: 0; margin-bottom: 10px;">Visual Design</h4>
            <div style="font-size: 24px; font-weight: bold; color: #6366f1; margin-bottom: 10px;">${Math.round(results.length * 0.45)}</div>
            <p style="font-size: 14px; color: #6b7280; margin: 0;">Websites need modern visual design updates to improve brand perception.</p>
          </div>
        </div>
      `;
    } else if (reportType === 'openers') {
      const topOpeners = results.slice(0, Math.min(10, results.length));
      
      reportContainer.innerHTML += `
        <h3 style="color: #4b5563; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">Personalized Openers</h3>
        <p style="line-height: 1.6; color: #4b5563; margin-bottom: 20px;">
          This report contains personalized conversation starters for outreach to the analyzed websites.
          Each opener is casual and conversational while still addressing specific website needs.
        </p>
        
        ${topOpeners.map((result, index) => `
          <div style="margin-bottom: 20px; padding: 15px; border-left: 3px solid #6366f1; background-color: #f9fafb; border-radius: 0 8px 8px 0;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
              <div>
                <div style="font-weight: bold;">${result.company || 'Unknown'}</div>
                <div style="font-size: 12px; color: #6b7280;">${result.url}</div>
              </div>
              <div style="display: inline-block; width: 36px; height: 36px; border-radius: 50%; background-color: ${
                result.fitScore >= 7 ? '#ecfdf5' : result.fitScore >= 5 ? '#fff7ed' : '#f3f4f6'
              }; color: ${
                result.fitScore >= 7 ? '#065f46' : result.fitScore >= 5 ? '#9a3412' : '#4b5563'
              }; text-align: center; line-height: 36px; font-weight: bold;">${result.fitScore || 'N/A'}</div>
            </div>
            <div style="font-style: italic; color: #4b5563; line-height: 1.6;">
              "${result.personalizedOpener || 'No personalized opener available'}"
            </div>
          </div>
        `).join('')}
        
        ${results.length > 10 ? `<p style="text-align: center; color: #6b7280; font-size: 14px;">Showing 10 of ${results.length} personalized openers. Export full data for all openers.</p>` : ''}
        
        <div style="display: flex; justify-content: space-between; margin-top: 30px;">
          <div style="text-align: center; padding: 15px; background-color: #f9fafb; border-radius: 8px; width: 30%;">
            <div style="font-size: 24px; font-weight: bold;">${results.length}</div>
            <div style="color: #6b7280; font-size: 14px;">Total Openers</div>
          </div>
          <div style="text-align: center; padding: 15px; background-color: #f9fafb; border-radius: 8px; width: 30%;">
            <div style="font-size: 24px; font-weight: bold; color: #065f46;">${highScoreResults}</div>
            <div style="color: #6b7280; font-size: 14px;">High Potential</div>
          </div>
          <div style="text-align: center; padding: 15px; background-color: #f9fafb; border-radius: 8px; width: 30%;">
            <div style="font-size: 24px; font-weight: bold; color: #9a3412;">${mediumScoreResults}</div>
            <div style="color: #6b7280; font-size: 14px;">Medium Potential</div>
          </div>
        </div>
      `;
    }
    
    // Footer
    reportContainer.innerHTML += `
      <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 12px; color: #6b7280;">
        Generated by WebAnalyzer | ${new Date().toLocaleDateString()} | ${results.length} websites analyzed
      </div>
    `;
    
    // Convert to PDF
    const canvas = await html2canvas(reportContainer, {
      scale: 1.5,
      logging: false,
      useCORS: true
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: 'a4'
    });
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const ratio = canvas.width / canvas.height;
    const canvasWidth = pdfWidth;
    const canvasHeight = canvasWidth / ratio;
    
    // Add pages if content is too long
    let heightLeft = canvasHeight;
    let position = 0;
    let pageCount = 1;
    
    pdf.addImage(imgData, 'PNG', 0, position, canvasWidth, canvasHeight);
    heightLeft -= pdfHeight;
    
    while (heightLeft > 0) {
      position = heightLeft - canvasHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, canvasWidth, canvasHeight);
      heightLeft -= pdfHeight;
      pageCount++;
    }
    
    // Save PDF
    pdf.save(`${reportType}-report-${new Date().toISOString().slice(0, 10)}.pdf`);
    
    if (callback) {
      callback({ success: true });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error generating PDF report:', error);
    if (callback) {
      callback({ success: false, error });
    }
    return { success: false, error };
  } finally {
    // Clean up
    if (document.body.contains(reportContainer)) {
      document.body.removeChild(reportContainer);
    }
  }
};

// Generate Word document (DOCX) report
export const generateDocxReport = async (results, reportType) => {
  try {
    // For DOCX, we'll generate HTML and use a library or blob to create the document
    // This is a simplified approach - in production you might use a more sophisticated library
    
    let content = `
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; }
          h1 { color: #6366f1; }
          .header { text-align: center; margin-bottom: 20px; }
          .stats { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .stat-card { text-align: center; padding: 10px; background-color: #f9fafb; border-radius: 8px; width: 23%; }
          .high { color: #065f46; }
          .medium { color: #9a3412; }
          .low { color: #f87171; }
          table { width: 100%; border-collapse: collapse; }
          th { background-color: #f9fafb; text-align: left; padding: 8px; border-bottom: 1px solid #e5e7eb; }
          td { padding: 8px; border-bottom: 1px solid #e5e7eb; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #6b7280; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>WebAnalyzer Report</h1>
          <h2>${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report</h2>
          <p>Generated on ${new Date().toLocaleDateString()}</p>
        </div>
    `;
    
    // Add report content based on type
    // (Similar to PDF content but with simpler formatting)
    
    content += `
        <div class="footer">
          Generated by WebAnalyzer | ${new Date().toLocaleDateString()} | ${results.length} websites analyzed
        </div>
      </body>
      </html>
    `;
    
    // Create blob and download
    const blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportType}-report-${new Date().toISOString().slice(0, 10)}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return { success: true };
  } catch (error) {
    console.error('Error generating DOCX report:', error);
    return { success: false, error };
  }
};

// Helper function to generate time ago format
export const formatTimeAgo = (date) => {
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  } catch (e) {
    return 'Unknown date';
  }
};